/**
 * iter234 — Cibles cliquables d'une ligne d'historique.
 *
 * Le backend (`historique_links.py`) renvoie `links: [{kind, id, label, url}]`.
 * Ici on rend :
 *   - le libellé de la ligne cliquable vers la cible principale (1er lien)
 *   - des pastilles pour les cibles secondaires (facture, devis, véhicule…)
 */
import { useNavigate } from "react-router-dom";
import { Wrench, Receipt, FileCheck, Car, KeyRound, ArrowUpRight } from "lucide-react";

const KIND_ICON = {
  or: Wrench,
  facture: Receipt,
  devis: FileCheck,
  vehicule: Car,
  pret: KeyRound,
};

const KIND_STYLE = {
  or: "border-[#52a675]/40 text-[#2d5f3f] hover:bg-[#52a675]/10",
  facture: "border-amber-300 text-amber-800 hover:bg-amber-50",
  devis: "border-blue-300 text-blue-800 hover:bg-blue-50",
  vehicule: "border-gray-300 text-gray-700 hover:bg-gray-50",
  pret: "border-violet-300 text-violet-800 hover:bg-violet-50",
};

export function HistoriqueDescription({ entry, className = "" }) {
  const navigate = useNavigate();
  const links = entry.links || [];
  const primary = links[0];
  if (!primary) {
    return <div className={`text-sm text-gray-900 ${className}`}>{entry.description}</div>;
  }
  return (
    <button
      type="button"
      onClick={() => navigate(primary.url)}
      title={`Ouvrir ${primary.label}`}
      className={`text-sm text-left text-gray-900 hover:text-[#2d5f3f] hover:underline decoration-[#52a675]/50 underline-offset-2 inline-flex items-start gap-1 group ${className}`}
      data-testid={`hist-open-${primary.kind}-${entry.id}`}
    >
      <span>{entry.description}</span>
      <ArrowUpRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-300 group-hover:text-[#52a675]" />
    </button>
  );
}

export default function HistoriqueLinks({ entry }) {
  const navigate = useNavigate();
  const links = (entry.links || []).slice(1);
  if (links.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`hist-links-${entry.id}`}>
      {links.map((l) => {
        const Icon = KIND_ICON[l.kind] || ArrowUpRight;
        return (
          <button
            key={`${l.kind}-${l.id}`}
            type="button"
            onClick={() => navigate(l.url)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-white text-[11px] font-medium transition-colors ${KIND_STYLE[l.kind] || KIND_STYLE.vehicule}`}
            data-testid={`hist-link-${l.kind}-${l.id}`}
          >
            <Icon className="w-3 h-3" /> {l.label}
          </button>
        );
      })}
    </div>
  );
}
